import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import CreateEventModal from '../components/CreateEventModal';
import EditEventModal from '../components/EditEventModal';
import {
  Calendar,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  QrCode,
  Users,
  Award,
  MapPin,
  Clock,
  TrendingUp,
  CheckCircle,
  UserCheck,
  Star,
  UserX,
} from 'lucide-react';
import { Event } from '../types';
import { useAuth } from '../context/AuthContext';

const EventsPage: React.FC = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [showQRCode, setShowQRCode] = useState<string | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    upcoming: 0,
    ongoing: 0,
    completed: 0,
    totalAttendees: 0,
  });

  const isSuperAdmin = user?.role === 'super_admin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;

  useEffect(() => {
    fetchEvents();
    if (isAdmin) {
      fetchStats();
    }
    // eslint-disable-next-line
  }, [statusFilter, categoryFilter]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: 1,
        limit: 100,
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await axios.get('/v1/events', { params });

      const loadedEvents = response.data?.data?.events || response.data?.data || [];
      setEvents(Array.isArray(loadedEvents) ? loadedEvents : []);
    } catch (error: any) {
      console.error('Failed to load events:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to load events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/v1/events/statistics');
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleDeleteEvent = async (eventId: number, eventTitle: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${eventTitle}"?\n\nThis action cannot be undone.\n\nType 'DELETE' in the next prompt to confirm.`
    );

    if (!confirmed) return;

    const confirmText = window.prompt('Type DELETE to confirm deletion:');
    if (confirmText !== 'DELETE') {
      toast.info('Deletion cancelled');
      return;
    }

    try {
      await axios.delete(`/v1/events/${eventId}`);
      toast.success(`Event "${eventTitle}" deleted successfully!`);
      fetchEvents();
      if (isAdmin) fetchStats();
    } catch (error: any) {
      console.error('Failed to delete event:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to delete event');
    }
  };

  const handleSearch = () => {
    fetchEvents();
  };

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    fetchEvents();
    if (isAdmin) fetchStats();
  };

  const handleEditSuccess = () => {
    setEditingEvent(null);
    fetchEvents();
  };

  const handleViewQR = (qrCode: string) => {
    setShowQRCode(qrCode);
  };

  const filteredEvents = events.filter((event) => {
    const matchesSearch =
      !searchTerm ||
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getStatusBadge = (status: string) => {
    const badges: { [key: string]: { bg: string; text: string; label: string } } = {
      upcoming: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Upcoming' },
      ongoing: { bg: 'bg-green-100', text: 'text-green-700', label: 'Live Now' },
      completed: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Completed' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' },
    };
    const badge = badges[status] || badges.upcoming;
    return (
      <span className={`px-3 py-1 ${badge.bg} ${badge.text} text-xs font-semibold rounded-full`}>
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-secondary-50 pb-8">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-secondary-900 mb-2">Events Management</h1>
                <p className="text-sm text-secondary-600">Create and manage federation events</p>
              </div>
              {isAdmin && (
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="btn btn-primary flex items-center gap-2"
                >
                  <Plus size={20} />
                  Create Event
                </button>
              )}
            </div>

            {/* Statistics (Admin Only) */}
            {isAdmin && (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                <div className="card bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">Total Events</p>
                      <p className="text-3xl font-bold">{stats.total}</p>
                    </div>
                    <Calendar size={40} className="opacity-50" />
                  </div>
                </div>

                <div className="card bg-gradient-to-br from-green-500 to-green-600 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">Upcoming</p>
                      <p className="text-3xl font-bold">{stats.upcoming}</p>
                    </div>
                    <TrendingUp size={40} className="opacity-50" />
                  </div>
                </div>

                <div className="card bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">Ongoing</p>
                      <p className="text-3xl font-bold">{stats.ongoing}</p>
                    </div>
                    <Clock size={40} className="opacity-50" />
                  </div>
                </div>

                <div className="card bg-gradient-to-br from-gray-500 to-gray-600 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">Completed</p>
                      <p className="text-3xl font-bold">{stats.completed}</p>
                    </div>
                    <CheckCircle size={40} className="opacity-50" />
                  </div>
                </div>

                <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm opacity-90">Total Attendees</p>
                      <p className="text-3xl font-bold">{stats.totalAttendees}</p>
                    </div>
                    <Users size={40} className="opacity-50" />
                  </div>
                </div>
              </div>
            )}

            {/* Search and Filters */}
            <div className="card">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
                    <input
                      type="text"
                      placeholder="Search events..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      className="input pl-10 w-full"
                    />
                  </div>
                </div>

                <div>
                  <div className="relative">
                    <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="input pl-10 w-full"
                    >
                      <option value="all">All Status</option>
                      <option value="upcoming">Upcoming</option>
                      <option value="ongoing">Ongoing</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                <div>
                  <div className="relative">
                    <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="input pl-10 w-full"
                    >
                      <option value="all">All Categories</option>
                      <option value="workshop">Workshop</option>
                      <option value="seminar">Seminar</option>
                      <option value="social">Social</option>
                      <option value="sports">Sports</option>
                      <option value="community_service">Community Service</option>
                      <option value="fundraising">Fundraising</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Events List */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="spinner"></div>
              <span className="ml-3 text-secondary-600">Loading events...</span>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="card text-center py-16">
              <Calendar size={64} className="mx-auto mb-4 text-secondary-300" />
              <p className="text-secondary-500 mb-4">No events found</p>
              {isAdmin && (
                <button onClick={() => setIsCreateModalOpen(true)} className="btn btn-primary">
                  Create Your First Event
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEvents.map((event) => (
                <div key={event.id} className="card hover:shadow-xl transition-shadow overflow-hidden">
                  {/* Event Brochure Image */}
                  {(event.brochure_url || event.brochureUrl) && (
                    <div className="relative w-full h-48 -mt-6 -mx-6 mb-4">
                      <img
                        src={event.brochure_url || event.brochureUrl}
                        alt={event.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    </div>
                  )}

                  {/* Event Header */}
                  <div className="flex justify-between items-start mb-4">
                    {getStatusBadge(event.status)}
                    {isAdmin && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingEvent(event)}
                          className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                          title="Edit Event"
                        >
                          <Edit size={18} className="text-blue-600" />
                        </button>
                        {event.event_qr_code && (
                          <button
                            onClick={() => handleViewQR(event.event_qr_code!)}
                            className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                            title="View QR Code"
                          >
                            <QrCode size={18} className="text-green-600" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteEvent(event.id, event.title)}
                          className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                          title="Delete Event"
                        >
                          <Trash2 size={18} className="text-red-600" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Event Title */}
                  <h3 className="text-xl font-bold text-secondary-900 mb-2">{event.title}</h3>

                  {/* Event Description */}
                  {event.description && (
                    <p className="text-sm text-secondary-600 mb-4">{event.description}</p>
                  )}

                  {/* Event Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-secondary-600">
                      <Calendar size={16} className="text-blue-500" />
                      <span>{formatDate(event.event_date)}</span>
                    </div>

                    {event.event_time && (
                      <div className="flex items-center gap-2 text-sm text-secondary-600">
                        <Clock size={16} className="text-green-500" />
                        <span>{event.event_time}</span>
                      </div>
                    )}

                    {event.location && (
                      <div className="flex items-center gap-2 text-sm text-secondary-600">
                        <MapPin size={16} className="text-red-500" />
                        <span className="truncate">{event.location}</span>
                      </div>
                    )}
                  </div>

                  {/* Event Category */}
                  {event.category && (
                    <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full mb-4">
                      {event.category.replace('_', ' ').toUpperCase()}
                    </span>
                  )}

                  {/* RSVP Counts - For Planning */}
                  {console.log('Event:', event.title, 'Status:', event.status, 'RSVP Confirmed:', event.rsvp_confirmed, 'Type:', typeof event.rsvp_confirmed)}
                  {event.status === 'upcoming' && (Number(event.rsvp_confirmed) > 0 || Number(event.rsvp_interested) > 0 || Number(event.rsvp_declined) > 0) && (
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="text-xs font-medium text-blue-900 mb-2">RSVP Responses</p>
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex items-center gap-1">
                          <UserCheck size={14} className="text-green-600" />
                          <span className="text-xs font-semibold text-green-700">
                            {event.rsvp_confirmed || 0} Going
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star size={14} className="text-yellow-600" />
                          <span className="text-xs font-semibold text-yellow-700">
                            {event.rsvp_interested || 0} Interested
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <UserX size={14} className="text-gray-600" />
                          <span className="text-xs font-semibold text-gray-700">
                            {event.rsvp_declined || 0} Not Going
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Event Stats */}
                  <div className="flex items-center gap-4 pt-4 border-t border-secondary-100">
                    <div className="flex items-center gap-2">
                      <Award size={18} className="text-amber-600" />
                      <span className="text-sm font-semibold text-amber-700">{event.points} Points</span>
                    </div>

                    {event.capacity && (
                      <div className="flex items-center gap-2">
                        <Users size={18} className="text-blue-600" />
                        <span className="text-sm font-semibold text-blue-700">
                          {event.attendance_count || 0}/{event.capacity}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {isCreateModalOpen && (
        <CreateEventModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={handleCreateSuccess} />
      )}

      {editingEvent && (
        <EditEventModal event={editingEvent} isOpen={!!editingEvent} onClose={() => setEditingEvent(null)} onSuccess={handleEditSuccess} />
      )}

      {/* QR Code Modal */}
      {showQRCode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowQRCode(null)}>
          <div className="bg-white rounded-xl p-8 max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-secondary-900 mb-4 text-center">Event QR Code</h3>
            <div className="flex justify-center mb-4">
              <img src={showQRCode} alt="Event QR Code" className="w-64 h-64" />
            </div>
            <p className="text-sm text-secondary-600 text-center mb-4">Scan this QR code to mark attendance</p>
            <button onClick={() => setShowQRCode(null)} className="btn btn-secondary w-full">
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default EventsPage;
